import { useEffect, useRef, useState } from "react";

/**
 * Parses a stat string like "240+", "12.5GW", "$100Ks", "100%", "~40%"
 * into a numeric target plus the prefix/suffix to display around the count.
 */
const parseStat = (raw: string) => {
  const match = raw.match(/-?\d+(\.\d+)?/);
  if (!match) {
    return { prefix: raw, target: 0, suffix: "", decimals: 0 };
  }
  const numStr = match[0];
  const idx = raw.indexOf(numStr);
  const prefix = raw.slice(0, idx);
  const suffix = raw.slice(idx + numStr.length);
  const decimals = (numStr.split(".")[1] ?? "").length;
  return { prefix, target: parseFloat(numStr), suffix, decimals };
};

const formatValue = (value: number, decimals: number) =>
  decimals > 0 ? value.toFixed(decimals) : Math.round(value).toLocaleString();

/**
 * Animates a numeric value from 0 to the parsed target when the returned
 * ref enters the viewport. Returns the formatted display string.
 */
export const useCountUp = (raw: string, duration = 1600) => {
  const ref = useRef<HTMLElement | null>(null);
  const { prefix, target, suffix, decimals } = parseStat(raw);
  const [display, setDisplay] = useState(`${prefix}${formatValue(0, decimals)}${suffix}`);
  const startedRef = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || startedRef.current) return;

    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setDisplay(raw);
      startedRef.current = true;
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || startedRef.current) return;
          startedRef.current = true;
          const start = performance.now();
          const tick = (now: number) => {
            const t = Math.min(1, (now - start) / duration);
            // easeOutCubic
            const eased = 1 - Math.pow(1 - t, 3);
            const current = target * eased;
            setDisplay(`${prefix}${formatValue(current, decimals)}${suffix}`);
            if (t < 1) requestAnimationFrame(tick);
            else setDisplay(raw);
          };
          requestAnimationFrame(tick);
          observer.disconnect();
        });
      },
      { threshold: 0.4 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [raw, target, prefix, suffix, decimals, duration]);

  return { ref, display };
};
